import React, { useState } from "react";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";

const sectorColors = {
  retail: "var(--color-primary)",
  tech: "#A78BFA",
  manufacturing: "var(--color-accent)",
  financial: "var(--color-success)",
  black_market: "var(--color-error)",
};

const sectorIcons = {
  retail: "ShoppingCart",
  tech: "Cpu",
  manufacturing: "Factory",
  financial: "Landmark",
  black_market: "Skull",
};

const riskColors = { Low: "var(--color-success)", Medium: "var(--color-accent)", High: "var(--color-error)" };

const availableBusinesses = [
  { id: "ab1", name: "Downtown Boutique", sector: "retail", purchasePrice: 85000, expectedRevenue: 18000, operatingCost: 9500, roiPeriod: "~5 months", risk: "Low", description: "A trendy fashion boutique in the city center with steady foot traffic and loyal customer base.", employees: 8, level: 1, staffingLevel: 70, inventoryLevel: 65, securityLevel: 40 },
  { id: "ab2", name: "NexGen Software", sector: "tech", purchasePrice: 220000, expectedRevenue: 45000, operatingCost: 22000, roiPeriod: "~6 months", risk: "Medium", description: "A growing software development firm specializing in fintech solutions with recurring SaaS revenue.", employees: 15, level: 1, staffingLevel: 80, inventoryLevel: 30, securityLevel: 60 },
  { id: "ab3", name: "Ironworks Foundry", sector: "manufacturing", purchasePrice: 310000, expectedRevenue: 62000, operatingCost: 38000, roiPeriod: "~13 months", risk: "Medium", description: "Heavy industrial manufacturing plant producing steel components for construction and automotive sectors.", employees: 45, level: 1, staffingLevel: 75, inventoryLevel: 80, securityLevel: 55 },
  { id: "ab4", name: "Apex Capital Fund", sector: "financial", purchasePrice: 500000, expectedRevenue: 110000, operatingCost: 45000, roiPeriod: "~8 months", risk: "High", description: "A private equity fund managing high-yield investment portfolios with aggressive growth strategies.", employees: 12, level: 1, staffingLevel: 90, inventoryLevel: 20, securityLevel: 75 },
  { id: "ab5", name: "Shadow Imports LLC", sector: "black_market", purchasePrice: 150000, expectedRevenue: 55000, operatingCost: 28000, roiPeriod: "~6 months", risk: "High", description: "Underground import/export operation with high margins but significant legal exposure and evidence risk.", employees: 6, level: 1, staffingLevel: 60, inventoryLevel: 70, securityLevel: 30 },
  { id: "ab6", name: "QuickMart Chain", sector: "retail", purchasePrice: 175000, expectedRevenue: 38000, operatingCost: 19000, roiPeriod: "~9 months", risk: "Low", description: "A chain of convenience stores with multiple locations providing stable recurring revenue streams.", employees: 22, level: 1, staffingLevel: 65, inventoryLevel: 75, securityLevel: 45 },
];

const AvailableBusinesses = ({ playerCash, onPurchase }) => {
  const [selectedSector, setSelectedSector] = useState("all");
  const sectors = ["all", "retail", "tech", "manufacturing", "financial", "black_market"];

  const filtered = selectedSector === "all" ? availableBusinesses : availableBusinesses?.filter(b => b?.sector === selectedSector);

  return (
    <div className="rounded-xl border p-4 md:p-5" style={{ background: "var(--color-card)", borderColor: "var(--color-border)" }}>
      <div className="flex items-center gap-2 mb-4">
        <Icon name="Store" size={16} color="var(--color-accent)" />
        <h3 className="font-heading text-sm font-semibold text-foreground">Available Acquisitions</h3>
        <span className="ml-auto font-caption text-xs px-2 py-0.5 rounded-full" style={{ background: "rgba(255,184,0,0.1)", color: "var(--color-accent)" }}>
          {filtered?.length} listings
        </span>
      </div>
      {/* Sector Filter */}
      <div className="flex gap-1.5 flex-wrap mb-4">
        {sectors?.map(s => {
          const color = s === "all" ? "var(--color-primary)" : sectorColors?.[s];
          const active = selectedSector === s;
          return (
            <button
              key={s}
              onClick={() => setSelectedSector(s)}
              className="px-2.5 py-1 rounded-md font-caption text-xs capitalize transition-all duration-200"
              style={{ background: active ? `${color}20` : "transparent", color: active ? color : "var(--color-muted-foreground)", border: `1px solid ${active ? `${color}40` : "transparent"}` }}
            >
              {s === "all" ? "All Sectors" : s?.replace("_", " ")}
            </button>
          );
        })}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {filtered?.map(biz => {
          const color = sectorColors?.[biz?.sector];
          const icon = sectorIcons?.[biz?.sector];
          const canAfford = playerCash >= biz?.purchasePrice;
          const netMonthly = biz?.expectedRevenue - biz?.operatingCost;
          return (
            <div key={biz?.id} className="rounded-xl border p-4 transition-all duration-200 hover:border-opacity-60" style={{ background: "rgba(255,255,255,0.02)", borderColor: `${color}25` }}>
              <div className="flex items-start gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${color}15` }}>
                  <Icon name={icon} size={16} color={color} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading text-sm font-semibold text-foreground truncate">{biz?.name}</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="font-caption text-xs capitalize" style={{ color }}>{biz?.sector?.replace("_", " ")}</span>
                    <span className="font-caption text-xs px-1.5 py-0.5 rounded" style={{ background: `${riskColors?.[biz?.risk]}15`, color: riskColors?.[biz?.risk] }}>{biz?.risk} Risk</span>
                  </div>
                </div>
              </div>
              <p className="font-caption text-xs text-muted-foreground mb-3 line-clamp-2">{biz?.description}</p>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div className="rounded-lg p-2 text-center" style={{ background: "rgba(239,68,68,0.08)" }}>
                  <div className="font-caption text-xs text-muted-foreground">Price</div>
                  <div className="font-heading text-xs font-bold" style={{ color: "var(--color-error)" }}>${biz?.purchasePrice?.toLocaleString()}</div>
                </div>
                <div className="rounded-lg p-2 text-center" style={{ background: "rgba(16,185,129,0.08)" }}>
                  <div className="font-caption text-xs text-muted-foreground">Net/mo</div>
                  <div className="font-heading text-xs font-bold" style={{ color: "var(--color-success)" }}>${netMonthly?.toLocaleString()}</div>
                </div>
              </div>
              <Button
                variant={canAfford ? "default" : "outline"}
                size="sm"
                fullWidth
                disabled={!canAfford}
                iconName={canAfford ? "ShoppingBag" : "Lock"}
                iconPosition="left"
                onClick={() => onPurchase(biz)}
              >
                {canAfford ? "Acquire Business" : "Insufficient Funds"}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export { availableBusinesses };
export default AvailableBusinesses;